import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Autoplay from 'embla-carousel-autoplay';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import { Search, Store, MapPin, Map } from 'lucide-react';
import { getBanners, getMerchants } from '../lib/salinguntungApi';

const MerchantsPage = () => {
  const [banners, setBanners] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getBanners().then(setBanners).catch(() => setBanners([]));
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(true);
      setError(null);
      getMerchants({ keyword })
        .then((data) => setMerchants(data?.data || []))
        .catch(() => setError('Gagal memuat data merchant. Silakan coba lagi.'))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [keyword]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-16 md:pt-20">
        {banners.length > 0 && (
          <section className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <Carousel opts={{ loop: true }} plugins={[Autoplay({ delay: 4000 })]}>
              <CarouselContent>
                {banners.map((banner) => {
                  const slide = (
                    <div className="relative overflow-hidden rounded-2xl shadow-lg">
                      <img
                        src={banner.image_url}
                        alt={banner.title || 'Banner promosi'}
                        className="w-full h-[180px] md:h-[320px] object-cover"
                      />
                      {banner.title && (
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent flex items-end">
                          <h3 className="text-white font-bold text-lg md:text-2xl p-6">{banner.title}</h3>
                        </div>
                      )}
                    </div>
                  );
                  return (
                    <CarouselItem key={banner.id}>
                      {banner.link_url ? (
                        <a href={banner.link_url} target="_blank" rel="noopener noreferrer">
                          {slide}
                        </a>
                      ) : (
                        slide
                      )}
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              {banners.length > 1 && (
                <>
                  <CarouselPrevious className="left-2" />
                  <CarouselNext className="right-2" />
                </>
              )}
            </Carousel>
          </section>
        )}

        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-md mx-auto mb-10 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Cari nama atau alamat merchant..."
                className="pl-10"
              />
            </div>
            <Link
              to="/merchants/map"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#2D9CDB] transition-colors"
            >
              <Map className="h-4 w-4" />
              Peta
            </Link>
          </div>

          {error && (
            <p className="text-center text-red-600 mb-8">{error}</p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-64 rounded-xl" />
              ))}
            </div>
          ) : merchants.length === 0 ? (
            <p className="text-center text-slate-500 py-12">Belum ada merchant yang ditemukan.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {merchants.map((merchant) => (
                <Link key={merchant.id} to={`/merchants/${merchant.code}`}>
                  <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 h-full overflow-hidden">
                    <div className="h-40 bg-slate-100 overflow-hidden">
                      {merchant.banner || merchant.logo ? (
                        <img
                          src={merchant.banner || merchant.logo}
                          alt={merchant.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Store className="h-10 w-10 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-bold text-slate-900 mb-1 truncate">{merchant.name}</h3>
                      {merchant.address && (
                        <p className="text-sm text-slate-500 flex items-start gap-1">
                          <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{merchant.address}</span>
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MerchantsPage;
